// Copyright (c) 2021. Sendanor <info@sendanor.fi>. All rights reserved.

import React from "react";
import ModalService, { ModalDestructor } from "../nor/ui/services/ModalService";
import ModalType from "../nor/ui/services/types/ModalType";
import LogService from "../nor/ts/LogService";
import { isFormFieldType } from "../nor/ui/types/FormFieldType";
import FieldBuilder from "../components/common/builder/field/FieldBuilder";
import PageBreakBuilder from "../components/common/builder/pageBreak/PageBreakBuilder";
import FormItem from "../nor/ui/types/FormItem";
import FormFieldModel from "../nor/ui/types/FormFieldModel";
import { ChangeCallback, EventCallback, VoidCallback } from "../nor/ts/interfaces/callbacks";
import FormItemType from "../nor/ui/types/FormItemType";
import PageBreakModel from "../nor/ui/types/items/PageBreakModel";
import Observer, { ObserverCallback, ObserverDestructor } from "../nor/ts/Observer";

const LOG = LogService.createLogger('EditFieldModal');

export enum EditFieldModalEvent {
    ITEM_CHANGED = "itemChanged"
}

export type EditFieldModalDestructor = ObserverDestructor;

export interface EditFieldModalProps {

    readonly parent             : typeof EditFieldModal;
    readonly changeItemCallback : ChangeCallback<FormItem>;
    readonly deleteItemCallback : VoidCallback;

}

export interface EditFieldModalState {

    readonly index : number | undefined;
    readonly item  : FormItem | undefined;

}

export class EditFieldModal extends React.Component<EditFieldModalProps, EditFieldModalState> {

    private static _fieldEditorModal : ModalDestructor | undefined;
    private static _openItem         : FormItem        | undefined;
    private static _openItemIndex    : number          | undefined;
    private static _observer         : Observer<EditFieldModalEvent> = new Observer<EditFieldModalEvent>("EditFieldModal");

    private readonly _changeCallback : EventCallback<EditFieldModalEvent>;

    private _changeListener : EditFieldModalDestructor | undefined;

    public static Event = EditFieldModalEvent;


    public constructor(props: EditFieldModalProps) {
        super(props);
        this.state = {
            index: props.parent.getItemIndex(),
            item: props.parent.getItem()
        };
        this._changeCallback = this._onChange.bind(this);
    }

    public componentDidMount (): void {

        this._changeListener = EditFieldModal.on(
            EditFieldModalEvent.ITEM_CHANGED,
            this._changeCallback
        )

    }

    public componentWillUnmount (): void {
        this.props.parent.closeComponent();
    }

    public render () {

        const item               = this.props.parent.getItem();
        if (!item) return null;

        const changeItemCallback = this.props.changeItemCallback;
        const deleteItemCallback = this.props.deleteItemCallback;

        return             (
            isFormFieldType(item.type) ? (
                <FieldBuilder
                    model={item as FormFieldModel}
                    onChange={changeItemCallback}
                    onDelete={deleteItemCallback}
                />
            ) : (
                item?.type === FormItemType.PAGE_BREAK ? (
                        <PageBreakBuilder
                            model={item as PageBreakModel}
                            onChange={changeItemCallback}
                            onDelete={deleteItemCallback}
                        />
                    ) :
                    null
            )
        )
    }

    private _onChange () {

        const newIndex = this.props.parent.getItemIndex();
        const newItem  = this.props.parent.getItem();

        if ( newIndex !== this.state.index || newItem !== this.state.index ) {
            this.setState({
                index: newIndex,
                item: newItem
            });
        }

    }


    public static on (
        name     : EditFieldModalEvent,
        callback : ObserverCallback<EditFieldModalEvent>
    ): EditFieldModalDestructor {
        return this._observer.listenEvent(name, callback);
    }

    public static destroy (): void {

        LOG.debug(`destroy: Closing modal`);
        this.closeModal();

        this._observer.destroy();

    }

    public static closeComponent () {

        this._setItem(undefined, undefined);

    }

    public static isOpen () : boolean {
        return this._fieldEditorModal !== undefined;
    }

    public static isOpenAs (item : FormItem) : boolean {
        return this._fieldEditorModal !== undefined && this._openItem === item;
    }

    public static isOpenAsIndex (index : number) : boolean {
        return this._fieldEditorModal !== undefined && this._openItemIndex === index;
    }

    public static getItemIndex () : number | undefined {
        return this._openItemIndex;
    }

    public static getItem () : FormItem | undefined {
        return this._openItem;
    }

    private static _setItem (index: number | undefined, item: FormItem | undefined) {

        if (this._openItem !== item || this._openItemIndex !== index ) {

            this._openItemIndex = index;
            this._openItem      = item;

            if (this._observer.hasCallbacks(EditFieldModalEvent.ITEM_CHANGED)) {
                this._observer.triggerEvent(EditFieldModalEvent.ITEM_CHANGED);
            }

        }

    }

    public static setItem (index: number, item: FormItem) {
        this._setItem(index, item);
    }

    public static closeModal () {
        if (this._fieldEditorModal) {
            LOG.debug(`closeModal: Closing modal`);
            this._fieldEditorModal();
            this._fieldEditorModal = undefined;
        } else {
            LOG.debug(`closeModal: Modal was not open`);
        }
        this._openItem = undefined;
    }

    public static openModal (
        index              : number,
        item               : FormItem,
        changeItemCallback : ChangeCallback<FormItem>,
        deleteItemCallback : VoidCallback
    ) {

        const isOpen = this.isOpen();

        if ( isOpen && this._openItemIndex === index ) {

            LOG.debug('openModal: Modal was already open: ', item);

            this._openItem = item;

            this._observer.triggerEvent(EditFieldModalEvent.ITEM_CHANGED);

        } else {

            if ( isOpen ) {
                LOG.debug('openModal: Closing previously open different item');
                this.closeModal();
            }

            LOG.debug('openModal: Creating field editor modal: ', item);

            this.setItem(index, item);

            this._fieldEditorModal = ModalService.createModal(
                (
                    <EditFieldModal
                        parent={this}
                        changeItemCallback={changeItemCallback}
                        deleteItemCallback={deleteItemCallback}
                    />
                ),
                ModalType.CENTER
            );

        }

    }

}

export default EditFieldModal;
